<script setup>
// Conexao com o Google Agenda (spec 010). Sem practitionerId e a do usuario logado (cabecalho da Agenda); com ele,
// a do profissional (formulario em Configuracoes > Atendimentos, so administrador). O Google devolve a pessoa
// para a tela com ?google_calendar=<resultado>; o aviso aparece aqui e a query e limpa.
import { computed, onMounted, ref } from 'vue';
import { onClickOutside, useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import GoogleCalendarAPI from 'dashboard/api/googleCalendar';
import GoogleCalendarPicker from './GoogleCalendarPicker.vue';
import { openGoogleConsent } from '../composables/openGoogleConsent';

const props = defineProps({
  practitionerId: { type: Number, default: null },
  // Botao compacto (icone + status) que abre as opcoes num popover; o padrao e a
  // faixa inline usada no formulario do profissional.
  compact: { type: Boolean, default: false },
});

const RESULT_MESSAGES = {
  connected: 'GOOGLE_CALENDAR.RESULT.CONNECTED',
  denied: 'GOOGLE_CALENDAR.RESULT.DENIED',
  missing_scope: 'GOOGLE_CALENDAR.RESULT.MISSING_SCOPE',
  error: 'GOOGLE_CALENDAR.RESULT.ERROR',
  calendar_list_granted: 'GOOGLE_CALENDAR.RESULT.CALENDAR_LIST_GRANTED',
  calendar_list_denied: 'GOOGLE_CALENDAR.RESULT.CALENDAR_LIST_DENIED',
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUserId = useMapGetter('getCurrentUserID');

const connection = ref(null);
const busy = ref(false);
const picker = ref(null);
const menuRoot = ref(null);
const menuOpen = ref(false);
onClickOutside(menuRoot, () => {
  menuOpen.value = false;
});
// Voltou do consentimento (da conexao inicial ou da listagem): se a conexao tem o escopo de listar agendas, abre o
// seletor assim que ela carregar.
const openPickerAfterLoad = ref(false);

const needsReconnect = computed(
  () => connection.value?.status === 'needs_reconnect'
);

const isOwnConnection = item =>
  props.practitionerId
    ? item.practitioner_id === props.practitionerId
    : item.owner_type === 'user' && item.user_id === currentUserId.value;

const load = async () => {
  try {
    const params = props.practitionerId
      ? { practitioner_id: props.practitionerId }
      : {};
    const { data } = await GoogleCalendarAPI.getConnections(params);
    connection.value = (data.data || []).find(isOwnConnection) || null;
  } catch {
    useAlert(t('GOOGLE_CALENDAR.LOAD_ERROR'));
  }
};

// So a tela que pediu a conexao mostra o resultado: a da Agenda (sem
// profissional) ou a do profissional que voltou na query.
const showResultFromQuery = () => {
  const message = RESULT_MESSAGES[route.query.google_calendar];
  if (!message) return;
  const queryPractitioner = Number(route.query.practitioner_id) || null;
  if (queryPractitioner !== props.practitionerId) return;

  useAlert(t(message));
  openPickerAfterLoad.value = ['connected', 'calendar_list_granted'].includes(
    route.query.google_calendar
  );
  const { google_calendar: _result, ...query } = route.query;
  router.replace({ query });
};

// O consentimento abre em outra aba; quando a pessoa volta para esta, a conexao e relida.
useEventListener(window, 'focus', () => {
  if (!connection.value || needsReconnect.value) load();
});

const connect = async () => {
  if (!window.confirm(t('GOOGLE_CALENDAR.UNVERIFIED_HINT'))) return;
  busy.value = true;
  try {
    await openGoogleConsent(async () => {
      const { data } = await GoogleCalendarAPI.authorize({
        practitionerId: props.practitionerId,
      });
      return data.url;
    });
  } catch {
    useAlert(t('GOOGLE_CALENDAR.ACTION_ERROR'));
  } finally {
    busy.value = false;
  }
};

const disconnect = async () => {
  if (!window.confirm(t('GOOGLE_CALENDAR.CONFIRM_DISCONNECT'))) return;
  busy.value = true;
  try {
    await GoogleCalendarAPI.disconnect(connection.value.id);
    connection.value = null;
    useAlert(t('GOOGLE_CALENDAR.DISCONNECTED'));
  } catch {
    useAlert(t('GOOGLE_CALENDAR.ACTION_ERROR'));
  } finally {
    busy.value = false;
  }
};

// Retornos do funil e atividades e titulo dos eventos: so na conexao do proprio usuario. O servidor
// devolve o estado gravado, que e o que a chave passa a mostrar.
const updateConnection = async changes => {
  busy.value = true;
  try {
    const { data } = await GoogleCalendarAPI.updateConnection(
      connection.value.id,
      changes
    );
    connection.value = data.data;
  } catch {
    useAlert(t('GOOGLE_CALENDAR.ACTION_ERROR'));
  } finally {
    busy.value = false;
  }
};

const updateSyncTasks = value => updateConnection({ syncTasks: value });
const updateShowEventTitles = value =>
  updateConnection({ showEventTitles: value });

const openPicker = () => {
  menuOpen.value = false;
  picker.value.open();
};

onMounted(async () => {
  showResultFromQuery();
  await load();
  if (openPickerAfterLoad.value && connection.value?.can_list_calendars)
    openPicker();
});
</script>

<template>
  <div ref="menuRoot" :class="compact ? 'relative' : ''">
    <button
      v-if="compact"
      type="button"
      class="flex items-center gap-1.5 h-8 px-2 text-xs font-semibold rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
      :aria-label="t('GOOGLE_CALENDAR.MENU_LABEL')"
      :aria-expanded="menuOpen"
      :title="
        connection
          ? t('GOOGLE_CALENDAR.CONNECTED_AS', {
              email: connection.google_email,
            })
          : t('GOOGLE_CALENDAR.STATUS_DISCONNECTED')
      "
      data-test="google-calendar-menu"
      @click="menuOpen = !menuOpen"
    >
      <span class="relative flex">
        <i class="i-lucide-calendar size-4" />
        <span
          class="absolute -top-0.5 -end-0.5 size-2 rounded-full border border-n-background"
          :class="
            connection && !needsReconnect
              ? 'bg-n-teal-9'
              : connection
                ? 'bg-n-amber-9'
                : 'bg-n-slate-8'
          "
          data-test="google-calendar-status-dot"
        />
      </span>
      <span class="hidden lg:inline">{{
        t('GOOGLE_CALENDAR.MENU_LABEL')
      }}</span>
      <i class="i-lucide-chevron-down size-3" />
    </button>
    <div
      v-show="!compact || menuOpen"
      :class="
        compact
          ? 'max-sm:fixed max-sm:inset-x-4 max-sm:w-auto sm:absolute sm:end-0 sm:w-72 sm:top-full z-50 mt-2 flex max-w-[calc(100vw-2rem)] flex-col items-stretch gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 shadow-lg'
          : 'flex flex-wrap items-center gap-2'
      "
    >
      <template v-if="!connection">
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-calendar-plus"
          class="font-semibold"
          :label="t('GOOGLE_CALENDAR.CONNECT')"
          :is-loading="busy"
          data-test="google-calendar-connect"
          @click="connect"
        />
      </template>
      <template v-else-if="needsReconnect">
        <span
          class="text-xs text-n-amber-11"
          data-test="google-calendar-needs-reconnect"
        >
          {{
            t('GOOGLE_CALENDAR.NEEDS_RECONNECT', {
              email: connection.google_email,
            })
          }}
        </span>
        <Button
          size="sm"
          color="amber"
          icon="i-lucide-refresh-cw"
          :label="t('GOOGLE_CALENDAR.RECONNECT')"
          :is-loading="busy"
          data-test="google-calendar-reconnect"
          @click="connect"
        />
      </template>
      <template v-else>
        <span
          class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
          data-test="google-calendar-connected"
        >
          <i class="i-lucide-calendar-check size-4 text-n-teal-11" />
          {{
            t('GOOGLE_CALENDAR.CONNECTED_AS', {
              email: connection.google_email,
            })
          }}
        </span>
        <label
          v-if="!practitionerId"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
          data-test="google-calendar-sync-tasks"
        >
          <Switch
            :model-value="connection.sync_tasks"
            :disabled="busy"
            @update:model-value="updateSyncTasks"
          />
          {{ t('GOOGLE_CALENDAR.SYNC_TASKS') }}
        </label>
        <label
          v-if="!practitionerId"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
          data-test="google-calendar-show-titles"
        >
          <Switch
            :model-value="connection.show_event_titles"
            :disabled="busy"
            @update:model-value="updateShowEventTitles"
          />
          {{ t('GOOGLE_CALENDAR.SHOW_EVENT_TITLES') }}
        </label>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-calendar-cog"
          :label="t('GOOGLE_CALENDAR.CHOOSE_CALENDAR')"
          :disabled="busy"
          data-test="google-calendar-choose"
          @click="openPicker"
        />
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          :label="t('GOOGLE_CALENDAR.DISCONNECT')"
          :is-loading="busy"
          data-test="google-calendar-disconnect"
          @click="disconnect"
        />
      </template>
    </div>
    <GoogleCalendarPicker
      v-if="connection"
      ref="picker"
      :connection-id="connection.id"
      :practitioner-id="practitionerId"
    />
  </div>
</template>
